import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Home from "@/pages/Home";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import Membership from "@/pages/Membership";
import Checkout from "@/pages/Checkout";
import OnboardingBiodata from "@/pages/OnboardingBiodata";
import TalentProfile from "@/pages/TalentProfile";
import TalentDirectory from "@/pages/TalentDirectory";
import DashboardTalent from "@/pages/DashboardTalent";
import DashboardCasting from "@/pages/DashboardCasting";
import AdminDashboard from "@/pages/AdminDashboard";
import CastingCalls from "@/pages/CastingCalls";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Services from "@/pages/Services";
import Work from "@/pages/Work";
import Join from "@/pages/Join";
import GenderPage from "@/pages/GenderPage";
import Legal from "@/pages/Legal";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import TalentDirectoryPage from "@/pages/TalentDirectoryPage";

function Protected({ children, role }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen" />;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/signup" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/pricing" element={<Membership />} />
      <Route path="/membership" element={<Membership />} />
      <Route path="/checkout/:orderId" element={<Protected><Checkout /></Protected>} />
      <Route path="/onboarding" element={<Protected><OnboardingBiodata /></Protected>} />
      <Route path="/talent" element={<TalentDirectory />} />
      <Route path="/search" element={<TalentDirectory />} />
      <Route path="/talent/:id" element={<TalentProfile />} />
      <Route path="/women" element={<TalentDirectoryPage slug="women" title="Women" subtitle="Distinctive faces and emerging female talent, curated for fashion, music, advertising and entertainment." />} />
      <Route path="/men" element={<TalentDirectoryPage slug="men" title="Men" subtitle="Selected male talent — models, actors and artists for editorial, commercial and screen." />} />
      <Route path="/women/:slug" element={<TalentProfile />} />
      <Route path="/men/:slug" element={<TalentProfile />} />
      <Route path="/new-faces" element={<TalentDirectoryPage slug="new-faces" title="New Faces" subtitle="Fresh perspectives. Distinctive personalities. The next generation of SV Talent." />} />
      <Route path="/models" element={<TalentDirectoryPage slug="models" title="Models" subtitle="Models represented by SV Talent Today." />} />
      <Route path="/actors" element={<TalentDirectoryPage slug="actors" title="Actors" subtitle="Actors & actresses for film, OTT and commercials." />} />
      <Route path="/creators" element={<TalentDirectoryPage slug="creators" title="Creators" subtitle="Digital creators and influencers." />} />
      <Route path="/casting" element={<CastingCalls />} />
      <Route path="/work" element={<Work />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/services" element={<Services />} />
      <Route path="/productions" element={<Services />} />
      <Route path="/join" element={<Join />} />
      <Route path="/privacy" element={<Legal kind="privacy" />} />
      <Route path="/privacy-policy" element={<Legal kind="privacy" />} />
      <Route path="/terms" element={<Legal kind="terms" />} />
      <Route path="/membership-terms" element={<Legal kind="membership-terms" />} />
      <Route path="/refund-policy" element={<Legal kind="refund" />} />
      <Route path="/dashboard" element={<Protected><DashboardTalent /></Protected>} />
      <Route path="/dashboard/talent" element={<Protected><DashboardTalent /></Protected>} />
      <Route path="/dashboard/casting" element={<Protected role="casting"><DashboardCasting /></Protected>} />
      <Route path="/admin" element={<Protected role="admin"><AdminDashboard /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-center" richColors theme="dark" />
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
  );
}
